import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { Analytics } from '../mru/analytics.model';
import { LocService } from '../shared-context/loc.service';

@Component({
    selector: 'ohp-analytics-description',
    templateUrl: './analytics-description.component.html',
    animations: [
        trigger('fadeIn', [
            transition('inactive => active', [
                style({ opacity: 0 }),
                animate('500ms 0ms linear', style({ opacity: 1 }))
            ])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnalyticsDescriptionComponent implements OnChanges {
    private static readonly spentTimeLessThanStringThreshold = 1;

    @Input() public analytics: Analytics;

    @Output() public readonly updateAriaLabel: EventEmitter<string>;

    public generalInfo: string;
    public detailedViewInfo: string;

    public state: 'inactive' | 'active';

    constructor(public locService: LocService) {
        this.updateAriaLabel = new EventEmitter<string>();
    }

    public ngOnChanges() {
        this.state = this.analytics ? 'active' : 'inactive';

        if (this.analytics) {
            const totalViewsLabel = this.formatSingularOrPluralString(this.analytics.viewCount, this.locService.DocumentAnalyticsSingleTotalView, this.locService.DocumentAnalyticsMultipleTotalViews);
            const avgTimeSpentLabel = this.locService.format(this.locService.DocumentAnalyticsAvgTimeSpent, this.getAvgTimeSpent(this.analytics.averageTimeSpentMs));
            const avgCompletionLabel = this.locService.format(this.locService.DocumentAnalyticsAvgCompletion, this.locService.localizedNumberString(Math.floor(100 * this.analytics.averageCompletionRatio)));

            const glancedReadsLabel = this.locService.format(this.locService.DocumentAnalyticsGlancedViews, this.locService.localizedNumberString(this.analytics.glancedReads));
            const quickReadsLabel = this.formatSingularOrPluralString(this.analytics.quickReads, this.locService.DocumentAnalyticsSingleQuickRead, this.locService.DocumentAnalyticsMultipleQuickReads);
            const deepReadsLabel = this.formatSingularOrPluralString(this.analytics.deepReads, this.locService.DocumentAnalyticsSingleDeepRead, this.locService.DocumentAnalyticsMultipleDeepReads);

            this.generalInfo = this.locService.format(this.locService.DocumentAnalyticsTotalViewsAvgTimeSpentAndAvgCompletionFormat, totalViewsLabel, avgTimeSpentLabel, avgCompletionLabel);
            this.detailedViewInfo = this.locService.format(this.locService.DocumentAnalyticsDetailedViewsInfoFormat, glancedReadsLabel, quickReadsLabel, deepReadsLabel);

            this.updateDescription();
        }
    }

    private getAvgTimeSpent(averageTimeSpentMs: number): string {
        const timeInMinutes = averageTimeSpentMs / 60000;
        return (timeInMinutes < AnalyticsDescriptionComponent.spentTimeLessThanStringThreshold)
            ? this.locService.format(this.locService.DocumentAnalyticsLessThanAvgTimeSpent, this.locService.localizedNumberString(AnalyticsDescriptionComponent.spentTimeLessThanStringThreshold))
            : this.locService.format(this.locService.DocumentAnalyticsAvgTimeSpentMinutes, this.locService.localizedNumberString(Math.round(timeInMinutes)));
    }

    private formatSingularOrPluralString(value: number, singularFormat: string, pluralFormat: string): string {
        return this.locService.format(value === 1 ? singularFormat : pluralFormat, this.locService.localizedNumberString(value));
    }

    private updateDescription() {
        const description = this.locService.format(
            this.locService.DocumentDescriptiveAriaLabelAnalytics,
            this.generalInfo,
            this.detailedViewInfo
        );
        this.updateAriaLabel.emit(description);
    }
}
